import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import BreadCrumb from '../components/Common/BreadCrumb';
import { monthlyThemeService } from '../services';
import { MonthlyTheme, MonthlyThemeInstruction } from '../types';

const UPLOADS_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/api\/?$/, '');

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const emptyInstruction = (): MonthlyThemeInstruction => ({
  icon: '',
  description: '',
});

const resolveIconUrl = (icon?: string) => {
  const value = String(icon || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${UPLOADS_BASE}/uploads/${value.replace(/^\/+/, '')}`;
};

type InstructionFormItem = MonthlyThemeInstruction & {
  iconFile?: File | null;
  iconPreviewUrl?: string;
};

const CURRENT_YEAR = new Date().getFullYear();

const MonthlyThemePage: React.FC = () => {
  const [themes, setThemes] = useState<MonthlyTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<MonthlyTheme | null>(null);

  const [form, setForm] = useState<{
    year: number;
    month: number;
    title: string;
    description: string;
    isActive: boolean;
    instructions: InstructionFormItem[];
  }>({
    year: CURRENT_YEAR,
    month: 1,
    title: '',
    description: '',
    isActive: true,
    instructions: [{ ...emptyInstruction(), iconFile: null, iconPreviewUrl: '' }],
  });

  useEffect(() => {
    loadThemes();
  }, []);

  const monthNameMap = useMemo(() => {
    const map = new Map<number, string>();
    MONTHS.forEach((m) => map.set(m.value, m.label));
    return map;
  }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      const data = await monthlyThemeService.getAll(false);
      setThemes(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load monthly themes');
    } finally {
      setLoading(false);
    }
  };

  const clearInstructionPreviewUrls = (items: InstructionFormItem[]) => {
    items.forEach((item) => {
      if (item.iconPreviewUrl) {
        URL.revokeObjectURL(item.iconPreviewUrl);
      }
    });
  };

  const openAdd = () => {
    setEditingTheme(null);
    clearInstructionPreviewUrls(form.instructions);
    setForm({
      year: CURRENT_YEAR,
      month: 1,
      title: '',
      description: '',
      isActive: true,
      instructions: [{ ...emptyInstruction(), iconFile: null, iconPreviewUrl: '' }],
    });
    setModalOpen(true);
  };

  const openEdit = (theme: MonthlyTheme) => {
    setEditingTheme(theme);
    setForm({
      year: theme.year ?? CURRENT_YEAR,
      month: theme.month,
      title: theme.title,
      description: theme.description,
      isActive: theme.isActive,
      instructions:
        Array.isArray(theme.instructions) && theme.instructions.length > 0
          ? theme.instructions.map((it) => ({
              icon: it.icon || '',
              description: it.description || '',
              iconFile: null,
              iconPreviewUrl: '',
            }))
          : [{ ...emptyInstruction(), iconFile: null, iconPreviewUrl: '' }],
    });
    setModalOpen(true);
  };

  const setInstructionField = (
    index: number,
    key: keyof InstructionFormItem,
    value: string,
  ) => {
    setForm((prev) => {
      const nextInstructions = [...prev.instructions];
      nextInstructions[index] = { ...nextInstructions[index], [key]: value };
      return { ...prev, instructions: nextInstructions };
    });
  };

  const addInstruction = () => {
    setForm((prev) => ({
      ...prev,
      instructions: [
        ...prev.instructions,
        { ...emptyInstruction(), iconFile: null, iconPreviewUrl: '' },
      ],
    }));
  };

  const removeInstruction = (index: number) => {
    setForm((prev) => {
      const removing = prev.instructions[index];
      if (removing?.iconPreviewUrl) {
        URL.revokeObjectURL(removing.iconPreviewUrl);
      }
      const next = prev.instructions.filter((_, i) => i !== index);
      return {
        ...prev,
        instructions: next.length
          ? next
          : [{ ...emptyInstruction(), iconFile: null, iconPreviewUrl: '' }],
      };
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Title and description are required');
      return;
    }

    const cleanInstructions = form.instructions
      .map((it) => ({
        icon: (it.icon || '').trim(),
        description: (it.description || '').trim(),
        iconFile: it.iconFile || null,
      }))
      .filter((it) => it.description);

    const hasInvalidInstruction = cleanInstructions.some((it) => !it.icon && !it.iconFile);
    if (hasInvalidInstruction) {
      toast.error('Each instruction must include icon image');
      return;
    }

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('year', String(Number(form.year)));
      payload.append('month', String(Number(form.month)));
      payload.append('title', form.title.trim());
      payload.append('description', form.description.trim());
      payload.append('isActive', String(Boolean(form.isActive)));

      let fileIndex = 0;
      const instructionsPayload = cleanInstructions.map((it) => {
        const base: Record<string, any> = { description: it.description };
        if (it.icon) base.icon = it.icon;
        if (it.iconFile) {
          base.iconFileIndex = fileIndex;
          payload.append('instructionIcons', it.iconFile);
          fileIndex += 1;
        }
        return base;
      });
      payload.append('instructions', JSON.stringify(instructionsPayload));

      if (editingTheme?._id) {
        await monthlyThemeService.update(editingTheme._id, payload);
        toast.success('Monthly theme updated');
      } else {
        await monthlyThemeService.create(payload);
        toast.success('Monthly theme created');
      }

      clearInstructionPreviewUrls(form.instructions);
      setModalOpen(false);
      loadThemes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save monthly theme');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this monthly theme?')) return;
    setDeletingId(id);
    try {
      await monthlyThemeService.delete(id);
      toast.success('Monthly theme deleted');
      loadThemes();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete monthly theme');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Monthly Theme" pageTitle="Home Management" />

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                      <i className="ri-calendar-2-line me-2" />
                      Monthly Theme
                    </h5>
                    <Button color="primary" onClick={openAdd}>
                      <i className="ri-add-line me-1" />
                      Add Monthly Theme
                    </Button>
                  </div>
                  <p className="text-muted small mb-4">
                    Admin can configure month and year theme title, description and instructions.
                  </p>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : themes.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light">
                      <i className="ri-calendar-line display-4 text-muted" />
                      <p className="mt-2 mb-0">No monthly themes yet</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Month</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>Instructions</th>
                            <th>Status</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {themes.map((theme) => (
                            <tr key={theme._id}>
                              <td>{theme.year ?? '—'}</td>
                              <td>{monthNameMap.get(theme.month) || theme.month}</td>
                              <td>{theme.title}</td>
                              <td style={{ maxWidth: 280 }}>{theme.description}</td>
                              <td>{theme.instructions?.length || 0}</td>
                              <td>
                                <span className={`badge ${theme.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                  {theme.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="text-end">
                                <Button color="light" size="sm" className="me-2" onClick={() => openEdit(theme)}>
                                  <i className="ri-edit-2-line" />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  outline
                                  disabled={deletingId === theme._id}
                                  onClick={() => handleDelete(theme._id)}
                                >
                                  {deletingId === theme._id ? <Spinner size="sm" /> : <i className="ri-delete-bin-line" />}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal
        isOpen={modalOpen}
        toggle={() => {
          clearInstructionPreviewUrls(form.instructions);
          setModalOpen(false);
        }}
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            clearInstructionPreviewUrls(form.instructions);
            setModalOpen(false);
          }}
        >
          {editingTheme ? 'Edit Monthly Theme' : 'Add Monthly Theme'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSave}>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    min={2000}
                    max={2100}
                    value={form.year}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, year: Number(e.target.value) }))
                    }
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Month</Label>
                  <Input
                    type="select"
                    value={form.month}
                    onChange={(e) => setForm((prev) => ({ ...prev, month: Number(e.target.value) }))}
                    required
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Title</Label>
                  <Input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Renewal & New Beginnings"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the monthly theme..."
                required
              />
            </FormGroup>

            <FormGroup check className="mb-3">
              <Input
                id="theme-active"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <Label for="theme-active" check>
                Active
              </Label>
            </FormGroup>

            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Instructions</h6>
              <Button type="button" color="light" size="sm" onClick={addInstruction}>
                <i className="ri-add-line me-1" />
                Add Instruction
              </Button>
            </div>
            <p className="text-muted small mb-3">
              Add icon + text for each instruction shown in the app.
            </p>

            {form.instructions.map((instruction, index) => (
              <Card key={index} className="mb-3 border">
                <CardBody className="py-3 px-3 px-md-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="fw-semibold">Instruction {index + 1}</span>
                    <Button
                      type="button"
                      color="danger"
                      outline
                      size="sm"
                      onClick={() => removeInstruction(index)}
                      title="Remove instruction"
                    >
                      <i className="ri-delete-bin-line me-1" />
                      Remove
                    </Button>
                  </div>
                  <Row className="g-3">
                    <Col md={4}>
                      <Label className="mb-2 fw-medium">Icon image</Label>
                      <div
                        className="d-flex align-items-center justify-content-center border rounded bg-light mb-2"
                        style={{ minHeight: 92 }}
                      >
                        {(instruction.iconPreviewUrl || instruction.icon) ? (
                          <img
                            src={
                              instruction.iconPreviewUrl ||
                              resolveIconUrl(instruction.icon)
                            }
                            alt="Instruction icon"
                            style={{
                              width: 56,
                              height: 56,
                              objectFit: 'cover',
                              borderRadius: 10,
                              border: '1px solid #e9ebec',
                              padding: 2,
                              background: '#fff',
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="text-muted small text-center px-2">
                            <i className="ri-image-line d-block fs-4 mb-1" />
                            No icon selected
                          </div>
                        )}
                      </div>
                      <Input
                        id={`instruction-icon-${index}`}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          setForm((prev) => {
                            const nextInstructions = [...prev.instructions];
                            const oldPreview = nextInstructions[index]?.iconPreviewUrl;
                            if (oldPreview) {
                              URL.revokeObjectURL(oldPreview);
                            }
                            nextInstructions[index] = {
                              ...nextInstructions[index],
                              iconFile: file,
                              iconPreviewUrl: file ? URL.createObjectURL(file) : '',
                            };
                            return { ...prev, instructions: nextInstructions };
                          });
                        }}
                      />
                      {instruction.iconFile ? (
                        <small className="text-muted d-block mt-1 text-truncate">{instruction.iconFile.name}</small>
                      ) : (
                        <small className="text-muted d-block mt-1">PNG, JPG, WEBP, GIF or SVG</small>
                      )}
                    </Col>
                    <Col md={8}>
                      <Label className="mb-2 fw-medium">Instruction description</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        value={instruction.description}
                        onChange={(e) => setInstructionField(index, 'description', e.target.value)}
                        placeholder="Write instruction text"
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                type="button"
                color="light"
                onClick={() => {
                  clearInstructionPreviewUrls(form.instructions);
                  setModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {editingTheme ? 'Update' : 'Create'}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default MonthlyThemePage;
